// frontend/src/pages/Login.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../context/use-auth";
import { showToast } from "../utils/toast";
import { resendVerification } from "../services/auth";
import { MAINTENANCE_LOCK, isEmailAllowed } from "@/utils/maintenance";
import { wakeBackend } from '@/utils/wake-backend';
import Spinner from "../components/ui/Spinner";
import Button from "@/components/ui/Button";


export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const navigate = useNavigate();

  const { t } = useTranslation();

  // Wake backend on page load to avoid cold-start on login
  useEffect(() => {
    wakeBackend();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Maintenance gate: block everyone except allowed emails
    if (MAINTENANCE_LOCK && !isEmailAllowed(email)) {
      showToast(t("auth.login.maintenance_blocked"), "info");
      return; // do NOT call login()
    }

    setLoading(true);
    setUnverifiedEmail(null);
    setResendDone(false);

    try {
      const success = await login(email, password);
      if (success) {
        navigate("/dashboard");
      } else {
        showToast(t("auth.login.not_successful"), "error");
      }
    } catch (err: any) {
      const msg = (err?.message || t("auth.login.error")) as string;
      const isUnverified = msg.toLowerCase().includes("not verified");
      if (isUnverified) {
        setUnverifiedEmail(email.trim());
        showToast(t("auth.login.error_unverified"), "error");
      } else {
        showToast(msg, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    try {
      setResending(true);
      await resendVerification(unverifiedEmail);
      setResendDone(true);
      showToast(t("auth.login.verification_sent"), "success");
    } catch {
      showToast(t("auth.login.verification_failed"), "error");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 px-4 pt-12 md:pt-16 lg:pt-0 lg:py-8 lg:flex-1 lg:grid lg:place-items-center">

      <div className="mx-auto w-full max-w-md bg-white shadow-md rounded px-8 py-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("common.email")}</label>
            <input
              type="email"
              className="standard-form-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t("common.password")}</label>
            <input
              type="password"
              className="standard-form-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="text-right mt-1">
              <a href="/forgot-password" className="text-xs text-elani-orange hover:underline">
                {t("auth.login.forgot_password")}
              </a>
            </div>
          </div>

          <Button
            type="submit"
            variant="blue"
            loading={loading}
            loadingText={t("auth.login.loading")}
            className="w-full"
          >
            {t("auth.login.login")}
          </Button>

          {/* Unverified email notice + resend (ELANI brand styling) */}
          {unverifiedEmail && (
            <div className="mt-3 rounded border-2 border-elani-orange bg-elani-orange-tint p-3 text-sm text-gray-800">
              <div className="flex items-center justify-between gap-3">
                <span>{t("auth.login.unverified_notice")}</span>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || resendDone}
                  className="px-3 py-1 rounded bg-elani-orange hover:bg-elani-orange-dark text-white disabled:opacity-50 flex items-center"
                >
                  {resending ? (
                    <>
                      <Spinner size="xs" className="mr-2" aria-label={t("auth.login.a11y_sending_label")} />
                      {t("auth.login.sending")}
                    </>
                  ) : resendDone ? (
                    t("auth.login.sent")
                  ) : (
                    t("auth.login.resend")
                  )}
                </button>
              </div>
            </div>
          )}

        {/* Maintenance lock notice */}
        {MAINTENANCE_LOCK && (
          <div className="mt-3 rounded border-2 border-elani-orange bg-elani-orange-tint p-3 text-sm text-gray-800">
            <div className="flex items-center justify-between gap-3">
              {t("auth.login.maintenance_message")}
            </div>
          </div>
        )}

          <div className="text-xs text-gray-500 text-center mt-4">
            <Trans
              i18nKey="auth.login.consent"
              components={{
                tos: <Link to="/terms" className="hover:underline" />,
                privacy: <Link to="/privacy" className="hover:underline" />
              }}
              values={{
                tosLabel: t("auth.login.terms"),
                privacyLabel: t("auth.login.privacy")
              }}
            />
          </div>

          <div className="text-center text-sm mt-2">
            {t("auth.login.new_user_prefix")} <a href="/register" className="text-elani-orange hover:underline">{t("auth.login.sign_up")}</a>
          </div>
        </form>
      </div>
    </div>
  );
}